import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../api/config';

const TableCard = React.memo(({ item, onStatusToggle, onDelete, index }) => {
  const cardRef = useRef(null);
  
  useEffect(() => {
    const card = cardRef.current;
    if (card) {
      card.style.transform = 'scale(0) translateY(50px)';
      card.style.opacity = '0';
      
      setTimeout(() => {
        card.style.transition = 'transform 0.5s, opacity 0.3s';
        card.style.transform = 'scale(1) translateY(0)';
        card.style.opacity = '1';
      }, 50 + index * 50);
    }
  }, []);

  const handleStatusToggle = () => {
    onStatusToggle(item._id, item.status);
  };

  return (
    <div 
      ref={cardRef} 
      className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
    >
      <div className="mb-4">
        <div className="flex justify-between items-center">
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-2 rounded-lg">
            <span className="text-xl font-bold text-gray-800">Table {item.tableNumber}</span>
          </div>
          <div 
            className={`flex items-center px-3 py-2 rounded-full ${
              item.status === 'Available' 
                ? 'bg-gradient-to-r from-green-50 to-emerald-50' 
                : 'bg-gradient-to-r from-red-50 to-rose-50'
            }`}
          >
            <div 
              className={`w-2 h-2 rounded-full mr-2 ${
                item.status === 'Available' ? 'bg-green-500' : 'bg-red-500'
              }`}
            ></div>
            <span 
              className={`text-sm font-semibold ${
                item.status === 'Available' ? 'text-green-700' : 'text-red-700'
              }`}
            >
              {item.status}
            </span>
          </div>
        </div>
      </div>
      <div className="flex gap-3">
        <button
          className={`flex-grow py-3 px-4 rounded-lg text-white font-medium transition-all hover:shadow-md ${
            item.status === 'Available' 
              ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700' 
              : 'bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700'
          }`}
          onClick={handleStatusToggle}
        >
          {item.status === 'Available' ? 'Set Occupied' : 'Set Available'}
        </button>
        <button
          className="flex-none w-24 py-3 px-3 rounded-lg bg-gradient-to-r from-red-500 to-rose-600 text-white font-medium transition-all hover:shadow-md hover:from-red-600 hover:to-rose-700"
          onClick={() => onDelete(item._id)}
        >
          Delete
        </button>
      </div>
    </div>
  );
});

const LoadingOverlay = ({ visible }) => {
  if (!visible) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white p-6 rounded-xl shadow-xl flex flex-col items-center">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <span className="text-gray-700 font-medium">Processing...</span>
      </div>
    </div>
  );
};

const EmptyStateView = ({ onRefresh }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4">
    <div className="bg-blue-50 p-8 rounded-2xl text-center max-w-md">
      <h3 className="text-2xl font-bold text-gray-800 mb-2">No Tables Yet</h3>
      <p className="text-gray-600 mb-6">
        Create your first table using the form above or click refresh
      </p>
      <button 
        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 px-6 py-3 rounded-lg text-white font-medium transition-all shadow-md hover:shadow-lg"
        onClick={onRefresh}
      >
        Refresh Tables
      </button>
    </div>
  </div>
);

const TableManagement = () => {
  const { userToken } = useAuth();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [newTableNumber, setNewTableNumber] = useState('');
  const [bulkCreateData, setBulkCreateData] = useState({
    baseNumber: '',
    quantity: ''
  });
  const [scrollPosition, setScrollPosition] = useState(0);
  const headerRef = useRef(null);
  
  // Handle scroll for header animation
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Apply header animation based on scroll position
  useEffect(() => {
    if (headerRef.current) {
      const headerHeight = 120 - (scrollPosition / 100) * 40;
      const headerOpacity = 1 - (scrollPosition / 100) * 0.1;
      
      headerRef.current.style.height = `${Math.max(80, headerHeight)}px`;
      headerRef.current.style.opacity = Math.max(0.9, headerOpacity);
    }
  }, [scrollPosition]);

  const fetchTables = async () => {
    try {
      const response = await fetch(`${API_URL}/tables`, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch tables');
      const data = await response.json();
      setTables(data.sort((a, b) => a.tableNumber - b.tableNumber));
    } catch (err) {
      alert('Error: Failed to load tables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, [userToken]);

  const createTable = async () => {
    if (!newTableNumber.trim()) {
      alert('Error: Please enter a table number');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/tables`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({ tableNumber: parseInt(newTableNumber) })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }
      
      await fetchTables();
      setNewTableNumber('');
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const createBulkTables = async () => {
    if (!bulkCreateData.baseNumber.trim() || !bulkCreateData.quantity.trim()) {
      alert('Error: Please fill in all fields');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/tables/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({
          baseNumber: parseInt(bulkCreateData.baseNumber),
          quantity: parseInt(bulkCreateData.quantity)
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }
      
      await fetchTables();
      setBulkCreateData({ baseNumber: '', quantity: '' });
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const toggleTableStatus = async (tableId, currentStatus) => {
    setProcessing(true);
    try {
      const newStatus = currentStatus === 'Available' ? 'Occupied' : 'Available';
      const response = await fetch(`${API_URL}/tables/${tableId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) throw new Error('Failed to update table status');
      await fetchTables();
    } catch (err) {
      alert('Error: Failed to update table status');
    } finally {
      setProcessing(false);
    }
  };

  const deleteTable = async (tableId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this table?');
    
    if (confirmDelete) {
      setProcessing(true);
      try {
        const response = await fetch(`${API_URL}/tables/${tableId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${userToken}`
          }
        });
        
        if (!response.ok) throw new Error('Failed to delete table');
        await fetchTables();
      } catch (err) {
        alert('Error: Failed to delete table');
      } finally {
        setProcessing(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <span className="text-gray-700 font-medium">Loading tables...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <LoadingOverlay visible={processing} />
      <div 
        ref={headerRef} 
        className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 flex flex-col justify-end pb-10 transition-all"
      >
        <h1 className="text-3xl font-bold text-white">Table Management</h1>
        <p className="text-blue-100 mt-1">
          {tables.length} {tables.length === 1 ? 'Table' : 'Tables'} Present
        </p>
      </div>
      
      <div className="mt-[-20px] relative z-10 pt-5 pb-16">
        <div className="bg-white rounded-xl shadow-lg mx-4 p-6 mb-6 border border-gray-100">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <input
              className="flex-grow px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Table Number"
              value={newTableNumber}
              onChange={(e) => setNewTableNumber(e.target.value)}
              type="number"
              onKeyPress={(e) => e.key === 'Enter' && createTable()}
            />
            <button
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                !newTableNumber.trim() 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md hover:shadow-lg hover:from-blue-600 hover:to-indigo-700'
              }`}
              onClick={createTable}
              disabled={!newTableNumber.trim()}
            >
              Add Table
            </button>
          </div>

          <div className="h-px bg-gray-200 my-6"></div>

          <h2 className="text-xl font-bold text-gray-800 mb-4">Bulk Create</h2>
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                className="flex-grow px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Starting Number"
                value={bulkCreateData.baseNumber}
                onChange={(e) => setBulkCreateData(prev => ({ ...prev, baseNumber: e.target.value }))}
                type="number"
              />
              <input
                className="flex-grow px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Quantity"
                value={bulkCreateData.quantity}
                onChange={(e) => setBulkCreateData(prev => ({ ...prev, quantity: e.target.value }))}
                type="number"
                onKeyPress={(e) => e.key === 'Enter' && createBulkTables()}
              />
            </div>
            <button
              className={`w-full px-6 py-3 rounded-lg font-medium transition-all ${
                !bulkCreateData.baseNumber.trim() || !bulkCreateData.quantity.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md hover:shadow-lg hover:from-blue-600 hover:to-indigo-700'
              }`}
              onClick={createBulkTables}
              disabled={!bulkCreateData.baseNumber.trim() || !bulkCreateData.quantity.trim()}
            >
              Create Multiple Tables
            </button>
          </div>
        </div>

        <div className="px-4 pb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Restaurant Tables</h2>
          <div className="space-y-4">
            {tables.length > 0 ? (
              tables.map((item, index) => (
                <TableCard
                  key={item._id}
                  item={item}
                  index={index}
                  onStatusToggle={toggleTableStatus}
                  onDelete={deleteTable}
                />
              ))
            ) : (
              <EmptyStateView onRefresh={fetchTables} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableManagement;